import React, { useEffect, useMemo, useState } from "react";
import { post } from "../../../services/api";
import { validateTimeRange } from "../../../utils/timeValidation";
import {
  MAX_RECURRING_SHOWS,
  WEEKDAY_LABELS,
  buildOccurrenceDates,
  defaultWeekdaysMonSun,
  getEndExclusive,
  parseYmdLocal,
  weekdaysFromGetDay,
} from "../../../utils/recurrence";
import {
  getEffectiveSlotsForDate,
  parseTimeSlots,
} from "../../../../lib/barTimeSlots";
import { formatDateForInput } from "../../../utils/calendarDate";
import {
  civilDateTodayInZone,
  normalizeBarTimeZone,
} from "../../../utils/civilDateTime.js";

const ensureSeconds = (timeValue) => {
  if (!timeValue) return "";
  return timeValue.length === 5 ? `${timeValue}:00` : timeValue;
};

const buildDefaultFormState = (options) => {
  const {
    defaultDate,
    venueTimeZone,
    defaultTimeSlotId,
    timeSlotsForDate,
    defaultArtistId,
  } = options || {};
  const slot =
    defaultTimeSlotId &&
    (timeSlotsForDate || []).find(
      (timeSlot) => String(timeSlot.id) === String(defaultTimeSlotId)
    );

  const fallbackYmd = civilDateTodayInZone(
    normalizeBarTimeZone(venueTimeZone)
  );
  return {
    date: formatDateForInput(
      defaultDate != null && defaultDate !== ""
        ? defaultDate
        : fallbackYmd
    ),
    artistId: defaultArtistId ? String(defaultArtistId) : "",
    timeSlotId: slot ? String(slot.id) : "",
    startTime: "",
    endTime: "",
    isRecurring: false,
    intervalMode: "1",
    customWeeks: "2",
    weekdaysMonSun: defaultWeekdaysMonSun(),
    endPreset: "1m",
    endDate: "",
    basePay: "",
  };
};

const buildEvenMemberBasePayRecord = (members, total) => {
  const n = members.length;
  if (n === 0) return {};
  const floor = Math.floor(total / n);
  const remainder = total - floor * n;
  const owner =
    members.find((m) => m.role === "owner") || members[0];
  const ownerId = String(owner.user_id);
  const rec = {};
  for (const m of members) {
    const id = String(m.user_id);
    rec[id] = String(id === ownerId ? floor + remainder : floor);
  }
  return rec;
};

const sortMembersForDisplay = (members) => {
  if (!members?.length) return [];
  return [...members].sort((a, b) => {
    if (a.role === "owner" && b.role !== "owner") return -1;
    if (b.role === "owner" && a.role !== "owner") return 1;
    return (a.name || "").localeCompare(b.name || "");
  });
};

const AddShowModal = ({
  isOpen,
  onClose,
  selectedBar,
  artists,
  artistsLoading,
  refreshBarShows,
  defaultDate,
  venueTimeZone,
  defaultTimeSlotId,
  defaultArtistId,
}) => {
  const [formState, setFormState] = useState(() =>
    buildDefaultFormState({
      defaultDate,
      venueTimeZone,
      defaultTimeSlotId,
      timeSlotsForDate: [],
      defaultArtistId,
    })
  );
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useCustomTimes, setUseCustomTimes] = useState(false);
  const [splitBasePayByMember, setSplitBasePayByMember] = useState(false);
  const [memberBasePayByUserId, setMemberBasePayByUserId] = useState({});

  const timeSlotOptions = useMemo(() => {
    if (!selectedBar?.time_slots || !formState.date) return [];
    return getEffectiveSlotsForDate(selectedBar.time_slots, formState.date);
  }, [selectedBar?.time_slots, formState.date]);

  const namedSlotsAvailable = useMemo(() => {
    const p = parseTimeSlots(selectedBar?.time_slots);
    return p.enabled && timeSlotOptions.length > 0;
  }, [selectedBar?.time_slots, timeSlotOptions.length]);

  const artistOptions = useMemo(() => artists ?? [], [artists]);

  const selectedArtist = useMemo(() => {
    if (!formState.artistId) return null;
    return artistOptions.find(
      (a) => String(a.artist_id) === String(formState.artistId)
    );
  }, [artistOptions, formState.artistId]);

  const orderedMembers = useMemo(() => {
    const m = selectedArtist?.members;
    if (m?.length) return sortMembersForDisplay(m);
    const ids = selectedArtist?.member_user_ids;
    if (!ids?.length) return [];
    return ids.map((user_id, i) => ({
      user_id,
      name: `Member ${i + 1}`,
      role: "member",
    }));
  }, [selectedArtist?.members, selectedArtist?.member_user_ids]);

  const canSplitPay = useMemo(() => {
    if (orderedMembers.length > 1) return true;
    const ids = selectedArtist?.member_user_ids;
    return Array.isArray(ids) && ids.length > 1;
  }, [orderedMembers.length, selectedArtist?.member_user_ids]);

  const splitSum = useMemo(() => {
    if (!splitBasePayByMember || orderedMembers.length === 0) return 0;
    return orderedMembers.reduce((acc, m) => {
      const raw = memberBasePayByUserId[String(m.user_id)];
      const n = Number.parseInt(raw, 10);
      return acc + (Number.isNaN(n) ? 0 : n);
    }, 0);
  }, [splitBasePayByMember, orderedMembers, memberBasePayByUserId]);

  const recurrencePreviewDates = useMemo(() => {
    if (!formState.isRecurring || !formState.date) return [];
    return buildOccurrenceDates(formState.date, {
      intervalMode: formState.intervalMode,
      customWeeks: formState.customWeeks,
      weekdaysMonSun: formState.weekdaysMonSun,
      endPreset: formState.endPreset,
      endDateStr: formState.endDate,
    });
  }, [
    formState.isRecurring,
    formState.date,
    formState.intervalMode,
    formState.customWeeks,
    formState.weekdaysMonSun,
    formState.endPreset,
    formState.endDate,
  ]);

  const recurrenceCount = recurrencePreviewDates.length;
  const recurrenceOverCap =
    formState.isRecurring && recurrenceCount > MAX_RECURRING_SHOWS;
  const recurringSubmitBlocked =
    formState.isRecurring &&
    (recurrenceCount === 0 || recurrenceOverCap);

  const submitBusyLabel = useMemo(() => {
    if (formState.isRecurring && recurrenceCount > 1) {
      return "Creating recurring shows…";
    }
    return "Creating show…";
  }, [formState.isRecurring, recurrenceCount]);

  // Reset when opening or when defaults / bar change. Depends on selectedBar?.time_slots
  // (stable) rather than timeSlotOptions so a new array reference does not wipe the form.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const ymdOpen = formatDateForInput(
      defaultDate != null && defaultDate !== ""
        ? defaultDate
        : civilDateTodayInZone(normalizeBarTimeZone(venueTimeZone))
    );
    const slotsForOpen = getEffectiveSlotsForDate(
      selectedBar?.time_slots,
      ymdOpen
    );
    const tsParsed = parseTimeSlots(selectedBar?.time_slots);
    setFormState(
      buildDefaultFormState({
        defaultDate,
        venueTimeZone,
        defaultTimeSlotId,
        timeSlotsForDate: slotsForOpen,
        defaultArtistId,
      })
    );
    setFormError("");
    setUseCustomTimes(!(tsParsed.enabled && slotsForOpen.length > 0));
    setSplitBasePayByMember(false);
    setMemberBasePayByUserId({});
  }, [
    isOpen,
    defaultDate,
    venueTimeZone,
    defaultTimeSlotId,
    defaultArtistId,
    selectedBar?.time_slots,
    selectedBar?.bar_id,
  ]);

  useEffect(() => {
    if (!canSplitPay && splitBasePayByMember) {
      setSplitBasePayByMember(false);
      setMemberBasePayByUserId({});
    }
  }, [canSplitPay, splitBasePayByMember]);

  useEffect(() => {
    if (!isOpen) return;
    const slots = getEffectiveSlotsForDate(
      selectedBar?.time_slots,
      formState.date
    );
    const p = parseTimeSlots(selectedBar?.time_slots);
    if (!p.enabled || slots.length === 0) {
      setUseCustomTimes(true);
    }
  }, [isOpen, formState.date, selectedBar?.time_slots]);

  useEffect(() => {
    if (!isOpen) return;
    const slots = getEffectiveSlotsForDate(
      selectedBar?.time_slots,
      formState.date
    );
    const ok = slots.some(
      (s) => String(s.id) === String(formState.timeSlotId)
    );
    if (!useCustomTimes && formState.timeSlotId && !ok) {
      setFormState((prev) => ({ ...prev, timeSlotId: "" }));
    }
  }, [
    isOpen,
    formState.date,
    formState.timeSlotId,
    selectedBar?.time_slots,
    useCustomTimes,
  ]);

  useEffect(() => {
    if (!isOpen || !formState.isRecurring || !formState.date) return;
    const d = parseYmdLocal(formState.date);
    if (!d) return;
    const wd = d.getDay();
    setFormState((prev) => {
      if (!prev.isRecurring) return prev;
      return { ...prev, weekdaysMonSun: weekdaysFromGetDay(wd) };
    });
  }, [isOpen, formState.date, formState.isRecurring]);

  if (!isOpen) {
    return null;
  }

  const handleFormValueChange = (field) => (event) => {
    const { value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (formError) {
      setFormError("");
    }
  };

  const handleArtistChange = (event) => {
    const { value } = event.target;
    setFormState((prev) => ({
      ...prev,
      artistId: value,
    }));
    setSplitBasePayByMember(false);
    setMemberBasePayByUserId({});
    if (formError) {
      setFormError("");
    }
  };

  const handleDateChange = handleFormValueChange("date");

  const handleIsRecurringChange = (event) => {
    const { checked } = event.target;
    setFormState((prev) => {
      if (!checked) {
        return {
          ...prev,
          isRecurring: false,
          intervalMode: "1",
          customWeeks: "2",
          weekdaysMonSun: defaultWeekdaysMonSun(),
          endPreset: "1m",
          endDate: "",
        };
      }
      const d = parseYmdLocal(prev.date);
      const wd = d ? d.getDay() : 1;
      return {
        ...prev,
        isRecurring: true,
        weekdaysMonSun: weekdaysFromGetDay(wd),
      };
    });
    if (formError) {
      setFormError("");
    }
  };

  const handleIntervalModeChange = (event) => {
    setFormState((prev) => ({ ...prev, intervalMode: event.target.value }));
    if (formError) setFormError("");
  };

  const handleCustomWeeksChange = (event) => {
    const v = event.target.value.replace(/\D/g, "");
    setFormState((prev) => ({
      ...prev,
      customWeeks: v === "" ? "" : v,
    }));
    if (formError) setFormError("");
  };

  const handleEndPresetChange = (event) => {
    const value = event.target.value;
    setFormState((prev) => ({
      ...prev,
      endPreset: value,
      endDate: value === "on_date" ? prev.endDate : "",
    }));
    if (formError) setFormError("");
  };

  const handleEndDateChange = (event) => {
    setFormState((prev) => ({ ...prev, endDate: event.target.value }));
    if (formError) setFormError("");
  };

  const handleWeekdayToggle = (index) => (event) => {
    const { checked } = event.target;
    setFormState((prev) => {
      const next = [...prev.weekdaysMonSun];
      next[index] = checked;
      return { ...prev, weekdaysMonSun: next };
    });
    if (formError) setFormError("");
  };

  const handleTimeSlotChange = (event) => {
    const { value } = event.target;
    setFormState((prev) => ({
      ...prev,
      timeSlotId: value,
      startTime: "",
      endTime: "",
    }));
    if (formError) {
      setFormError("");
    }
  };

  const handleStartTimeChange = (event) => {
    const { value } = event.target;
    setFormState((prev) => ({
      ...prev,
      timeSlotId: "",
      startTime: value,
    }));
    if (formError) {
      setFormError("");
    }
  };

  const handleEndTimeChange = (event) => {
    const { value } = event.target;
    setFormState((prev) => ({
      ...prev,
      timeSlotId: "",
      endTime: value,
    }));
    if (formError) {
      setFormError("");
    }
  };

  const handleBasePayChange = (event) => {
    const { value } = event.target;
    const sanitized = value.replace(/\D/g, "");
    setFormState((prev) => ({
      ...prev,
      basePay: sanitized,
    }));
    if (formError) {
      setFormError("");
    }
  };

  const handleToggleCustomTimes = () => {
    if (formError) setFormError("");
    setFormState((s) => {
      if (!useCustomTimes) {
        return { ...s, timeSlotId: "" };
      }
      return { ...s, startTime: "", endTime: "" };
    });
    setUseCustomTimes((prev) => !prev);
  };

  const handleSplitToggle = (event) => {
    const { checked } = event.target;
    setSplitBasePayByMember(checked);
    if (checked && orderedMembers.length > 0) {
      const total = Number.parseInt(formState.basePay, 10);
      if (!Number.isNaN(total) && total >= 0) {
        setMemberBasePayByUserId(buildEvenMemberBasePayRecord(orderedMembers, total));
      } else {
        setMemberBasePayByUserId({});
      }
    } else {
      setMemberBasePayByUserId({});
    }
    if (formError) setFormError("");
  };

  const handleMemberBasePayChange = (userId) => (event) => {
    const sanitized = event.target.value.replace(/\D/g, "");
    setMemberBasePayByUserId((prev) => ({
      ...prev,
      [String(userId)]: sanitized,
    }));
    if (formError) setFormError("");
  };

  const closeModal = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedBar?.bar_id) {
      setFormError("You must select a bar before scheduling a show.");
      return;
    }

    if (!formState.date) {
      setFormError("Please choose a date for the show.");
      return;
    }

    if (!formState.artistId) {
      setFormError("Please select an artist for the show.");
      return;
    }

    let startTime = "";
    let endTime = "";

    if (namedSlotsAvailable && !useCustomTimes) {
      const slot = timeSlotOptions.find(
        (timeSlot) => String(timeSlot.id) === formState.timeSlotId
      );

      if (!slot) {
        setFormError("Select a venue time slot, or use custom start and end times.");
        return;
      }

      startTime = slot.start_time;
      endTime = slot.end_time;
    } else if (formState.startTime && formState.endTime) {
      const validation = validateTimeRange(
        formState.startTime,
        formState.endTime
      );
      if (!validation.isValid) {
        setFormError(validation.error);
        return;
      }
      if (validation.warning) {
        console.warn(validation.warning);
      }

      startTime = ensureSeconds(formState.startTime);
      endTime = ensureSeconds(formState.endTime);
    } else {
      setFormError("Enter both a start time and an end time.");
      return;
    }

    const splitModeActive =
      splitBasePayByMember && canSplitPay && orderedMembers.length > 1;

    let basePayValue;
    if (splitModeActive) {
      for (const m of orderedMembers) {
        const raw = memberBasePayByUserId[String(m.user_id)];
        const amt = Number.parseInt(raw, 10);
        if (raw === undefined || raw === "" || Number.isNaN(amt) || amt < 0) {
          setFormError("Enter a non-negative base pay amount for each member.");
          return;
        }
      }
      basePayValue = splitSum;
      if (Number.isNaN(basePayValue) || basePayValue < 0) {
        setFormError("Total base pay must be a non-negative amount.");
        return;
      }
    } else {
      basePayValue = Number.parseInt(formState.basePay, 10);
      if (
        Number.isNaN(basePayValue) ||
        basePayValue < 0 ||
        formState.basePay === ""
      ) {
        setFormError("Base pay is required and must be a non-negative amount.");
        return;
      }
    }

    if (!startTime || !endTime) {
      setFormError("A start time and an end time are required.");
      return;
    }

    let datesToCreate = [formState.date];

    if (formState.isRecurring) {
      const hasDay = formState.weekdaysMonSun.some(Boolean);
      if (!hasDay) {
        setFormError('Select at least one day under "Repeats on".');
        return;
      }

      if (formState.intervalMode === "custom") {
        const cw = Number.parseInt(formState.customWeeks, 10);
        if (
          formState.customWeeks === "" ||
          Number.isNaN(cw) ||
          cw < 1 ||
          cw > 12
        ) {
          setFormError("Enter a custom interval between 1 and 12 weeks.");
          return;
        }
      }

      if (formState.endPreset === "on_date") {
        if (!formState.endDate) {
          setFormError("Choose an end date for the series.");
          return;
        }
        const start = parseYmdLocal(formState.date);
        const end = parseYmdLocal(formState.endDate);
        if (!start || !end || end < start) {
          setFormError("End date must be on or after the series start date.");
          return;
        }
      }

      const exclusive = getEndExclusive(
        formState.date,
        formState.endPreset,
        formState.endDate
      );
      const startParsed = parseYmdLocal(formState.date);
      if (!exclusive || !startParsed || exclusive <= startParsed) {
        setFormError(
          "Adjust the end of the series so it runs for at least one day after the start date."
        );
        return;
      }

      datesToCreate = buildOccurrenceDates(formState.date, {
        intervalMode: formState.intervalMode,
        customWeeks: formState.customWeeks,
        weekdaysMonSun: formState.weekdaysMonSun,
        endPreset: formState.endPreset,
        endDateStr: formState.endDate,
      });

      if (datesToCreate.length === 0) {
        setFormError(
          "No shows match these recurrence settings. Adjust days, interval, or end date."
        );
        return;
      }

      if (datesToCreate.length > MAX_RECURRING_SHOWS) {
        setFormError(
          `This series would create ${datesToCreate.length} shows. The maximum is ${MAX_RECURRING_SHOWS}. Shorten the range, reduce how often it repeats, or pick fewer days.`
        );
        return;
      }
    }

    setIsSubmitting(true);
    setFormError("");

    try {

      let recurringGroupId = null;

      for (const dateValue of datesToCreate) {
        const artistForPayload = artistOptions.find(
          (a) => String(a.artist_id) === String(formState.artistId)
        );
        const payload = {
          bar_id: Number(selectedBar.bar_id),
          artist_id: Number(formState.artistId),
          date: dateValue,
          start_time: startTime,
          end_time: endTime,
          is_reoccurring: Boolean(formState.isRecurring),
          base_pay: basePayValue,
        };
        if (
          artistForPayload?.member_user_ids &&
          Array.isArray(artistForPayload.member_user_ids) &&
          artistForPayload.member_user_ids.length > 0
        ) {
          payload.member_user_ids = artistForPayload.member_user_ids;
        }

        if (splitModeActive) {
          payload.member_base_pay = orderedMembers.map((m) => ({
            user_id: Number(m.user_id),
            base_pay: Number.parseInt(
              memberBasePayByUserId[String(m.user_id)],
              10
            ),
          }));
        }

        if (recurringGroupId) {
          payload.recurring_group_id = recurringGroupId;
        }

        const response = await post("/shows", payload);

        if (!response?.success) {
          throw new Error(response?.error || "Failed to create show.");
        }

        if (!recurringGroupId && response.recurring_group_id) {
          recurringGroupId = response.recurring_group_id;
        }
      }

      await refreshBarShows(undefined, { silent: true });
      onClose();
    } catch (error) {
      setFormError(error.message || "Failed to create show.");
      console.error("Add show failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatSplitTotal = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  const splitModeActive =
    splitBasePayByMember && canSplitPay && orderedMembers.length > 1;

  const allSplitMembersFilled =
    splitModeActive &&
    orderedMembers.length > 0 &&
    orderedMembers.every((m) => {
      const raw = memberBasePayByUserId[String(m.user_id)];
      const amt = Number.parseInt(raw, 10);
      return raw !== undefined && raw !== "" && !Number.isNaN(amt) && amt >= 0;
    });

  return (
    <div className="add-show-modal-overlay" role="presentation">
      <div
        className="add-show-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-show-modal-title"
        aria-busy={isSubmitting ? "true" : "false"}
      >
        <div className="add-show-modal-header">
          <h2 id="add-show-modal-title">Add New Show</h2>
          <button
            type="button"
            className="add-show-modal-close"
            onClick={closeModal}
            aria-label="Close add show modal"
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>
        <form className="add-show-form" onSubmit={handleSubmit}>
          <div className="add-show-form-row">
            <label className="add-show-label">
              Date
              <input
                type="date"
                className="add-show-input"
                value={formState.date}
                onChange={handleDateChange}
                required
                disabled={isSubmitting}
              />
            </label>
            <label className="add-show-label">
              Artist
              <select
                className="add-show-input"
                value={formState.artistId}
                onChange={handleArtistChange}
                required
                disabled={
                  isSubmitting ||
                  artistsLoading ||
                  (!artistsLoading && artistOptions.length === 0)
                }
              >
                <option value="" disabled>
                  {artistsLoading ? "Loading artists..." : "Select an artist"}
                </option>
                {artistOptions.map((artist) => (
                  <option key={artist.artist_id} value={artist.artist_id}>
                    {artist.stage_name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {!artistsLoading && artistOptions.length === 0 && (
            <p className="add-show-helper">
              No artists are linked to this bar yet. Add artists from Management
              to schedule a show.
            </p>
          )}
          {namedSlotsAvailable && !useCustomTimes && (
            <div className="add-show-form-row add-show-form-row--single">
              <label className="add-show-label">
                Time Slot
                <select
                  className="add-show-input"
                  value={formState.timeSlotId}
                  onChange={handleTimeSlotChange}
                  disabled={isSubmitting}
                >
                  <option value="">Select a time slot</option>
                  {timeSlotOptions.map((slot) => (
                    <option key={slot.id} value={slot.id}>
                      {slot.name} ({slot.start_time?.slice(0, 5)} -{" "}
                      {slot.end_time?.slice(0, 5)})
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          {namedSlotsAvailable && (
            <div className="add-show-custom-times-row">
              <button
                type="button"
                className="add-show-custom-times-toggle"
                onClick={handleToggleCustomTimes}
                disabled={isSubmitting}
                aria-expanded={useCustomTimes}
              >
                {useCustomTimes
                  ? "Use a venue time slot instead"
                  : "Use custom start and end times instead"}
              </button>
            </div>
          )}
          {!namedSlotsAvailable && (
            <p className="add-show-helper">
              No named time slots for this day — enter custom start and end
              times.
            </p>
          )}
          {(useCustomTimes || !namedSlotsAvailable) && (
            <div className="add-show-form-row">
              <label className="add-show-label">
                Start Time
                <input
                  type="time"
                  className="add-show-input"
                  value={formState.startTime}
                  onChange={handleStartTimeChange}
                  disabled={isSubmitting}
                />
              </label>
              <label className="add-show-label">
                End Time
                <input
                  type="time"
                  className="add-show-input"
                  value={formState.endTime}
                  onChange={handleEndTimeChange}
                  disabled={isSubmitting}
                />
              </label>
            </div>
          )}
          {!splitModeActive && (
            <div className="add-show-form-row add-show-form-row--single">
              <label className="add-show-label">
                Base Pay (USD)
                <input
                  type="number"
                  className="add-show-input"
                  value={formState.basePay}
                  onChange={handleBasePayChange}
                  min="0"
                  step="1"
                  required
                  disabled={isSubmitting}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </label>
            </div>
          )}
          {canSplitPay && (
            <div className="add-show-split-pay">
              <div className="add-show-toggle-row">
                <span
                  className="add-show-toggle-label"
                  id="add-show-split-label"
                >
                  Split base pay by member
                </span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={splitBasePayByMember}
                    onChange={handleSplitToggle}
                    disabled={isSubmitting}
                    aria-labelledby="add-show-split-label"
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
              {splitBasePayByMember && orderedMembers.length > 0 && (
                <div className="add-show-member-splits">
                  {orderedMembers.map((m) => (
                    <label key={m.user_id} className="add-show-label">
                      {m.name}
                      {m.role === "owner" ? " (Owner)" : ""}
                      <input
                        type="number"
                        className="add-show-input"
                        value={memberBasePayByUserId[String(m.user_id)] ?? ""}
                        onChange={handleMemberBasePayChange(m.user_id)}
                        min="0"
                        step="1"
                        required
                        disabled={isSubmitting}
                        inputMode="numeric"
                        pattern="[0-9]*"
                      />
                    </label>
                  ))}
                  <p
                    className={
                      allSplitMembersFilled
                        ? "add-show-split-total add-show-split-total--ok"
                        : "add-show-split-total add-show-split-total--warn"
                    }
                  >
                    Total base pay: {formatSplitTotal(splitSum)}
                  </p>
                </div>
              )}
            </div>
          )}
          <div className="add-show-recurring">
            <div className="add-show-toggle-row">
              <span
                className="add-show-toggle-label"
                id="add-show-recurring-label"
              >
                Recurring
              </span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formState.isRecurring}
                  onChange={handleIsRecurringChange}
                  disabled={isSubmitting}
                  aria-labelledby="add-show-recurring-label"
                />
                <span className="toggle-slider" />
              </label>
            </div>
            {formState.isRecurring && (
              <div className="add-show-recurring-panel">
                <label className="add-show-label add-show-recurring-field">
                  Repeats
                  <select
                    className="add-show-input"
                    value={formState.intervalMode}
                    onChange={handleIntervalModeChange}
                    disabled={isSubmitting}
                  >
                    <option value="1">Every week</option>
                    <option value="2">Every 2 weeks</option>
                    <option value="3">Every 3 weeks</option>
                    <option value="4">Every 4 weeks</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>
                {formState.intervalMode === "custom" && (
                  <label className="add-show-label add-show-recurring-field">
                    Custom interval (weeks)
                    <input
                      type="number"
                      className="add-show-input"
                      min="1"
                      max="12"
                      step="1"
                      value={formState.customWeeks}
                      onChange={handleCustomWeeksChange}
                      disabled={isSubmitting}
                      inputMode="numeric"
                    />
                  </label>
                )}
                <div className="add-show-recurring-field">
                  <span className="add-show-weekday-heading">Repeats on</span>
                  <div
                    className="add-show-weekday-row"
                    role="group"
                    aria-label="Days of the week"
                  >
                    {WEEKDAY_LABELS.map((label, index) => (
                      <label
                        key={label}
                        className={
                          formState.weekdaysMonSun[index]
                            ? "add-show-weekday-chip add-show-weekday-chip--on"
                            : "add-show-weekday-chip"
                        }
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(
                            formState.weekdaysMonSun[index]
                          )}
                          onChange={handleWeekdayToggle(index)}
                          disabled={isSubmitting}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <label className="add-show-label add-show-recurring-field">
                  Repeat ends
                  <select
                    className="add-show-input"
                    value={formState.endPreset}
                    onChange={handleEndPresetChange}
                    disabled={isSubmitting}
                  >
                    <option value="1m">In one month</option>
                    <option value="3m">In three months</option>
                    <option value="6m">In six months</option>
                    <option value="1y">In one year</option>
                    <option value="on_date">On date</option>
                  </select>
                </label>
                {formState.endPreset === "on_date" && (
                  <label className="add-show-label add-show-recurring-field">
                    End date
                    <input
                      type="date"
                      className="add-show-input"
                      value={formState.endDate}
                      min={formState.date}
                      onChange={handleEndDateChange}
                      disabled={isSubmitting}
                    />
                  </label>
                )}
                <p
                  className={
                    recurrenceOverCap
                      ? "add-show-recurrence-summary add-show-recurrence-summary--warn"
                      : "add-show-recurrence-summary"
                  }
                >
                  {recurrenceCount === 0
                    ? "No matching dates with these settings."
                    : `This will schedule ${recurrenceCount} show${
                        recurrenceCount === 1 ? "" : "s"
                      }.`}
                  {recurrenceOverCap
                    ? ` The maximum per submission is ${MAX_RECURRING_SHOWS}.`
                    : ""}
                </p>
              </div>
            )}
          </div>
          {formError && (
            <div className="add-show-error" role="alert">
              {formError}
            </div>
          )}
          <div className="add-show-modal-actions">
            <button
              type="button"
              className="add-show-cancel-btn"
              onClick={closeModal}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="add-show-submit-btn"
              disabled={
                isSubmitting ||
                (!artistsLoading && artistOptions.length === 0) ||
                recurringSubmitBlocked
              }
            >
              {isSubmitting ? "Saving..." : "Submit"}
            </button>
          </div>
        </form>
        {isSubmitting && (
          <div
            className="add-show-modal-busy-overlay"
            role="status"
            aria-live="polite"
          >
            <div className="calendar-loading-indicator">
              <span
                className="calendar-loading-spinner"
                aria-hidden="true"
              />
              <span className="calendar-loading-text">{submitBusyLabel}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddShowModal;
